import { Card, CardBody, CardHeader, CardTitle } from '@/shared/components';
import { formatRelative } from '@/shared/lib/format';
import type { AuditLogResponse, UserResponse } from '@/shared/api';
import { auditActionLabel, auditEntityLabel } from '@/features/system/systemLabels';
import { userStatus } from '../userStatus';
import { UserActivityRow } from './UserActivityRow';
import { buildUserActivity } from './userActivity';

interface Props {
  user: UserResponse;
  auditEntries: AuditLogResponse[];
  /** Ob dieser Aufrufer das Änderungsprotokoll überhaupt lesen darf (Server-Policy). */
  canReadAuditLog: boolean;
  auditLoading: boolean;
  auditError: boolean;
  truncated: boolean;
}

/**
 * Aktivität eines Kontos aus vorhandenen Daten — Anmeldung und Änderungsprotokoll.
 *
 * <p>Das Protokoll ist serverseitig der Plattformverwaltung vorbehalten
 * (<c>RequirePlatformAdmin</c>). Ein Workspace-Administrator sieht deshalb nur die Zeitstempel
 * des Kontos selbst — und liest das auch so, statt einer Fläche zu begegnen, die dauerhaft
 * einen Fehler zeigt. Die Sichtbarkeit hier ist nur UX; die Autorität bleibt der Server.</p>
 *
 * <p>Weder IP-Adresse noch Browserkennung stehen in dieser Leiste. Sie liegen im Vertrag vor,
 * beantworten aber nicht die Frage dieser Fläche („was hat dieses Konto verändert") und
 * verwandelten eine Verwaltungsansicht in eine Aufenthaltsüberwachung.</p>
 */
export function UserActivityCard({
  user,
  auditEntries,
  canReadAuditLog,
  auditLoading,
  auditError,
  truncated,
}: Props) {
  const entries = buildUserActivity(user, canReadAuditLog ? auditEntries : [], {
    entity: auditEntityLabel,
    action: auditActionLabel,
  });
  const status = userStatus(user);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Aktivität</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-ink-soft mb-4 text-sm">
          {status === 'Invited'
            ? 'Die Einladung ist noch offen — dieses Konto hat sich nie angemeldet.'
            : user.lastLoginAt
              ? `Zuletzt angemeldet ${formatRelative(user.lastLoginAt)}.`
              : 'Dieses Konto hat sich noch nie angemeldet.'}
        </p>

        <ol className="border-line/60 space-y-4 border-l pl-4">
          {entries.map((entry) => (
            <UserActivityRow key={entry.id} entry={entry} />
          ))}
        </ol>

        {canReadAuditLog ? (
          <>
            {auditLoading && (
              <p className="text-ink-faint mt-4 text-xs">Änderungsprotokoll wird geladen…</p>
            )}
            {auditError && (
              <p className="text-sealwax mt-4 text-xs">
                Das Änderungsprotokoll konnte nicht geladen werden. Die Zeitstempel des Kontos
                stehen trotzdem oben.
              </p>
            )}
            {truncated && (
              <p className="text-ink-faint mt-4 text-xs">
                Nur die jüngsten Einträge. Das vollständige Protokoll steht unter „System ·
                Änderungsprotokoll".
              </p>
            )}
          </>
        ) : (
          <p className="text-ink-faint mt-4 text-xs">
            Das Änderungsprotokoll ist der Plattformverwaltung vorbehalten. Diese Leiste zeigt
            deshalb nur die Zeitstempel des Kontos.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
